/**
 * The arithmetic behind hand tracking and frame scheduling. Pure: no canvas,
 * no model, no clock, so scripts/hands-test.mjs can check every rule in Node.
 *
 * HANDS FROM THE POSE. The hand model looks for a palm first, and on a whole
 * 1080p frame a hand thirty feet away is a few pixels inside its 192 pixel
 * input: it is never found. So, the way MediaPipe Holistic does it, the pose
 * says where each wrist is, a square is cut around it sized from the forearm,
 * reaching past the wrist where the hand is, and the hand model is shown that
 * square. A small far hand then fills the model's input. A hand close to the
 * camera makes a square as big as the frame, and then the frame itself is used.
 * With no body in the picture at all (only arms, or only hands) the whole frame
 * is searched, so hands held up to the camera are still found.
 *
 * Movement only: nothing here reads a face or tells one person from another.
 */

export const HAND = {
  // Where the square sits: this many forearm lengths past the wrist, along the
  // line from the elbow. The hand extends past the wrist; a cocked wrist bends
  // it sideways, which the square's size covers.
  REACH: 0.35,
  // Side of the square, in forearm lengths. A hand is about three quarters of
  // a forearm long; the palm detector wants room around it.
  SIZE: 1.9,
  // A forearm pointed at the camera looks short in the picture. Its length is
  // never taken as less than these shares of body parts that rarely shorten at
  // the same moment: shoulder width, upper arm, torso.
  FROM_SHOULDERS: 0.65, FROM_UPPER_ARM: 0.85, FROM_TORSO: 0.5,
  MIN_PX: 48,           // the square never gets smaller than this many camera pixels
  MIN_VIS: 0.3,         // a wrist below this visibility is not looked for
  // A square wider than this share of the frame's short side is no longer a
  // zoom: the whole frame is searched instead.
  FULL_FRAC: 0.8,
  // Two squares this close (centre gap under this share of their mean size)
  // hold the same two hands, so they are merged into one.
  MERGE: 0.7,
};

const vis = (p) => (p ? (p.visibility === undefined ? 1 : p.visibility) : 0);
const ok = (p, min) => Boolean(p) && Number.isFinite(p.x) && Number.isFinite(p.y) && vis(p) >= min;
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * The forearm length to size a hand square from, in camera pixels: the longest
 * of what the forearm itself shows and what the shoulders, upper arm and
 * torso say it must be at least.
 */
export function forearmPx(lm, side, W, H, T = HAND) {
  const px = (p) => ({ x: p.x * W, y: p.y * H });
  const [s, e, w] = side === 'left' ? [11, 13, 15] : [12, 14, 16];
  const cands = [];
  if (ok(lm[e], T.MIN_VIS) && ok(lm[w], T.MIN_VIS)) cands.push(dist(px(lm[e]), px(lm[w])));
  if (ok(lm[11], T.MIN_VIS) && ok(lm[12], T.MIN_VIS)) cands.push(T.FROM_SHOULDERS * dist(px(lm[11]), px(lm[12])));
  if (ok(lm[s], T.MIN_VIS) && ok(lm[e], T.MIN_VIS)) cands.push(T.FROM_UPPER_ARM * dist(px(lm[s]), px(lm[e])));
  const hip = side === 'left' ? 23 : 24;
  if (ok(lm[s], T.MIN_VIS) && ok(lm[hip], T.MIN_VIS)) cands.push(T.FROM_TORSO * dist(px(lm[s]), px(lm[hip])));
  return cands.length ? Math.max(...cands) : 0;
}

/**
 * One square per wrist worth looking at, in camera pixels:
 * [{ side, cx, cy, size, wrist: {x, y} }]. A wrist that is not seen, or whose
 * square would sit mostly outside the frame, gets none.
 */
export function handBoxes(lm, W, H, T = HAND) {
  const out = [];
  if (!Array.isArray(lm) || lm.length < 23 || !W || !H) return out;
  for (const side of ['left', 'right']) {
    const [e, w, pinky, index] = side === 'left' ? [13, 15, 17, 19] : [14, 16, 18, 20];
    const wrist = lm[w];
    if (!ok(wrist, T.MIN_VIS)) continue;
    const fa = forearmPx(lm, side, W, H, T);
    if (!fa) continue;
    const wx = wrist.x * W, wy = wrist.y * H;
    // Which way the hand points: along the forearm when it is long enough to
    // say, else toward the pose model's own finger points, else nowhere.
    let dx = 0, dy = 0;
    const elbow = lm[e];
    const fl = ok(elbow, T.MIN_VIS) ? Math.hypot(wx - elbow.x * W, wy - elbow.y * H) : 0;
    if (fl > 0.3 * fa) { dx = (wx - elbow.x * W) / fl; dy = (wy - elbow.y * H) / fl; }
    else if (ok(lm[pinky], T.MIN_VIS) && ok(lm[index], T.MIN_VIS)) {
      const mx = ((lm[pinky].x + lm[index].x) / 2) * W - wx, my = ((lm[pinky].y + lm[index].y) / 2) * H - wy;
      const ml = Math.hypot(mx, my);
      if (ml > 1) { dx = mx / ml; dy = my / ml; }
    }
    const size = Math.max(T.MIN_PX, T.SIZE * fa);
    const cx = wx + dx * T.REACH * fa, cy = wy + dy * T.REACH * fa;
    // Mostly outside the picture: the hand is not in it.
    if (cx < -size / 2 || cy < -size / 2 || cx > W + size / 2 || cy > H + size / 2) continue;
    out.push({ side, cx, cy, size, wrist: { x: wx, y: wy } });
  }
  return out;
}

/** A square of side `size` around (cx, cy), moved inside a W by H frame. Never larger than the frame's short side. */
export function squareIn(cx, cy, size, W, H) {
  const s = Math.max(1, Math.min(size, W, H));
  const x = Math.min(Math.max(0, cx - s / 2), W - s);
  const y = Math.min(Math.max(0, cy - s / 2), H - s);
  return { x, y, w: s, h: s };
}

/**
 * The regions to show the hand model this frame, in camera pixels, each with
 * the sides it is expected to hold: [{ x, y, w, h, sides, full }].
 * Two squares that overlap become one around both hands; a square too big to
 * be a zoom becomes the whole frame.
 */
export function planHandCrops(boxes, W, H, T = HAND) {
  if (!boxes.length) return [];
  const full = { x: 0, y: 0, w: W, h: H, sides: boxes.map((b) => b.side), full: true };
  const short = Math.min(W, H);
  if (boxes.some((b) => b.size >= T.FULL_FRAC * short)) return [full];
  if (boxes.length === 2) {
    const [a, b] = boxes;
    const gap = Math.hypot(a.cx - b.cx, a.cy - b.cy);
    if (gap < T.MERGE * (a.size + b.size) / 2) {
      const x0 = Math.min(a.cx - a.size / 2, b.cx - b.size / 2), x1 = Math.max(a.cx + a.size / 2, b.cx + b.size / 2);
      const y0 = Math.min(a.cy - a.size / 2, b.cy - b.size / 2), y1 = Math.max(a.cy + a.size / 2, b.cy + b.size / 2);
      const size = Math.max(x1 - x0, y1 - y0);
      if (size >= T.FULL_FRAC * short) return [full];
      return [{ ...squareIn((x0 + x1) / 2, (y0 + y1) / 2, size, W, H), sides: ['left', 'right'], full: false }];
    }
  }
  return boxes.map((b) => ({ ...squareIn(b.cx, b.cy, b.size, W, H), sides: [b.side], full: false }));
}

/** The part of `r` that lies inside `bound`, or null. Both in camera pixels. */
export function clipRect(r, bound) {
  const x0 = Math.max(r.x, bound.x), y0 = Math.max(r.y, bound.y);
  const x1 = Math.min(r.x + r.w, bound.x + bound.w), y1 = Math.min(r.y + r.h, bound.y + bound.h);
  return x1 - x0 >= 8 && y1 - y0 >= 8 ? { ...r, x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
}

/**
 * Where `rect` (camera pixels) is drawn in a square canvas of side S: centred,
 * as large as fits, black around it. `region` is the camera pixel square the
 * whole canvas stands for, which is what fromRegion needs to map back.
 */
export function letterbox(rect, S) {
  const side = Math.max(rect.w, rect.h), k = S / side;
  const dw = rect.w * k, dh = rect.h * k;
  const cx = rect.x + rect.w / 2, cy = rect.y + rect.h / 2;
  return { dx: (S - dw) / 2, dy: (S - dh) / 2, dw, dh, region: { x: cx - side / 2, y: cy - side / 2, w: side, h: side } };
}

/**
 * Landmarks the model returned for a picture of `region` (camera pixels) back
 * to whole frame units. z is left as it is: it is depth about the hips (pose)
 * or the wrist (hand), not a position in the picture.
 */
export function fromRegion(points, region, W, H) {
  return points.map((p) => ({ ...p, x: (region.x + p.x * region.w) / W, y: (region.y + p.y * region.h) / H }));
}

/**
 * Which hand is whose. `found` are hands in whole frame units; `wrists` are
 * the pose's wrists in the same units, { left, right }, either may be null.
 * Returns [{ side, lm }]: each pose wrist takes the nearest hand, measured in
 * pixels, a hand found twice (two overlapping squares) is kept once, and a
 * hand nowhere near either wrist still counts, with side null, when there is
 * no pose to place it (hands close to the camera).
 */
export function assignHands(found, wrists, W = 1, H = 1) {
  const hands = [];
  // The same hand seen through two squares: its wrist lands in nearly the same place.
  for (const h of found) {
    const w0 = h.lm[0];
    const size = handSize(h.lm, W, H);
    const dup = hands.find((k) => Math.hypot((k.lm[0].x - w0.x) * W, (k.lm[0].y - w0.y) * H) < 0.35 * Math.max(size, handSize(k.lm, W, H)));
    if (dup) { if ((h.score ?? 0) > (dup.score ?? 0)) Object.assign(dup, h); continue; }
    hands.push({ ...h });
  }
  const sides = ['left', 'right'].filter((s) => wrists && wrists[s]);
  if (!sides.length) return hands.slice(0, 2).map((h) => ({ side: null, lm: h.lm, score: h.score ?? null }));
  const d = (h, s) => Math.hypot((h.lm[0].x - wrists[s].x) * W, (h.lm[0].y - wrists[s].y) * H);
  // Two hands and two wrists: the pairing with the smaller total distance.
  // Otherwise each wrist, nearest first, takes the nearest hand left.
  const out = [];
  if (hands.length >= 2 && sides.length === 2) {
    const [a, b] = hands.slice().sort((x, y) => Math.min(d(x, 'left'), d(x, 'right')) - Math.min(d(y, 'left'), d(y, 'right')));
    const straight = d(a, 'left') + d(b, 'right'), crossed = d(a, 'right') + d(b, 'left');
    const [la, ra] = straight <= crossed ? [a, b] : [b, a];
    out.push({ side: 'left', lm: la.lm, score: la.score ?? null }, { side: 'right', lm: ra.lm, score: ra.score ?? null });
    return out;
  }
  // Fewer hands than wrists, or the other way round: the closest pairs first.
  const pairs = [];
  hands.forEach((h, i) => sides.forEach((s) => pairs.push({ i, s, dd: d(h, s) })));
  pairs.sort((a, b) => a.dd - b.dd);
  const usedH = new Set(), usedS = new Set();
  for (const p of pairs) {
    if (usedH.has(p.i) || usedS.has(p.s)) continue;
    usedH.add(p.i); usedS.add(p.s);
    out.push({ side: p.s, lm: hands[p.i].lm, score: hands[p.i].score ?? null });
  }
  return out;
}

/** Wrist to middle finger knuckle, a steady measure of how big a hand is in the picture, pixels. */
export function handSize(lm, W = 1, H = 1) {
  const a = lm?.[0], b = lm?.[9];
  return a && b ? Math.hypot((a.x - b.x) * W, (a.y - b.y) * H) * 2 : 0;
}

/** The pose nearest a hip point (0..1), or the biggest pose when there is no hint. */
export function pickPose(poses, hint) {
  if (!Array.isArray(poses) || !poses.length) return null;
  const hip = (lm) => (lm[23] && lm[24] ? { x: (lm[23].x + lm[24].x) / 2, y: (lm[23].y + lm[24].y) / 2 } : lm[0] || null);
  const span = (lm) => (lm[11] && lm[12] ? Math.hypot(lm[11].x - lm[12].x, lm[11].y - lm[12].y) : 0);
  if (hint && Number.isFinite(hint.x) && Number.isFinite(hint.y)) {
    let best = null, bd = Infinity;
    for (const lm of poses) { const h = hip(lm); if (!h) continue; const dd = Math.hypot(h.x - hint.x, h.y - hint.y); if (dd < bd) { bd = dd; best = lm; } }
    if (best) return best;
  }
  return poses.reduce((a, b) => (span(b) > span(a) ? b : a));
}

/* ------------------------------------------------------------ one body, one pose
 * Asked for several bodies, the pose model sometimes hands back the same
 * person twice: a second skeleton laid over the first, a little shifted or
 * squeezed. On a clip with one teenager in it that happened on 25 frames of
 * 242 (2026-09-25), and each copy took a slot of its own in the tracker. The
 * real body then got matched to the copy's slot, the player's own slot went
 * empty, and the skeleton vanished for the 2.5 seconds the app waits before
 * it trusts a new body. So copies are dropped before anything else sees them.
 *
 * Two poses are one person when their upper bodies (nose, shoulders, elbows,
 * wrists, hips) lie on top of each other: the median gap between matching
 * points under SAME body widths. Two real people side by side or one behind
 * the other are a body width or more apart at most of those points. The
 * smaller of the two skeletons goes.
 *
 * The model also makes a second kind of copy: a smaller skeleton squeezed
 * inside the real one, sharing its head, sometimes facing the other way. On
 * the clip of a teenager walking up to the phone that was 74 frames in 1142
 * (2026-09-25), and the tracker then swapped the player's slot between the
 * body and the copy, so the skeleton blinked out mid shot. Two more rules
 * catch it, and both need both noses clearly seen:
 *   - the noses sit within NOSE body widths: two real heads cannot be in one
 *     place in the picture, a head behind a head is hidden;
 *   - or the noses are within NEST_NOSE body widths, the smaller skeleton lies
 *     at least NEST_IN inside the bigger one's outline, and its hips are at or
 *     above the bigger one's shoulders: a whole body inside somebody's head
 *     and shoulders is not a person.
 * Real people in the same clips were three or more body widths apart, nose to
 * nose (the father and daughter in the driveway clip).
 *
 * And a third: a player turning side on is sometimes read twice at once, once
 * side on and once square to the camera, the arms and shoulders landing in
 * different places but the trunk in the same place. On the clip of a
 * teenager shooting with his back to the phone that was every few seconds,
 * and the second reading kept a slot of its own that later took the player's
 * body when the clip jumped. So two trunks (the middle of shoulders and hips)
 * within TORSO_GAP body widths of each other, of lengths within TORSO_RATIO,
 * both clearly seen, are one person. Two real people cannot stand in the same
 * place; the father and daughter were two or more body widths apart.
 */
export const POSES = {
  SAME: 0.45,                             // median point gap, in body widths, under which two poses are one person
  POINTS: [0, 11, 12, 13, 14, 15, 16, 23, 24],
  NOSE: 0.15,                             // noses this close, in body widths: one person
  NEST_NOSE: 0.65,                        // a copy inside the body: noses within this, in body widths,
  NEST_IN: 0.7,                           // ...this share of its outline inside the bigger one's,
  NOSE_VIS: 0.5,                          // ...and both noses seen at least this well
  TORSO_GAP: 0.45,                        // trunk middles this close, in body widths,
  TORSO_RATIO: 0.6,                       // ...trunk lengths at least this alike,
  TORSO_VIS: 0.5,                         // ...and shoulders and hips seen at least this well: one person
};

/** The middle of the trunk and its length, in pixels, or null when it is not clearly seen. */
function trunk(lm, W, H, T) {
  const [a, b, c, d] = [lm[11], lm[12], lm[23], lm[24]];
  if (![a, b, c, d].every((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y) && vis(p) >= T.TORSO_VIS)) return null;
  const sx = ((a.x + b.x) / 2) * W, sy = ((a.y + b.y) / 2) * H, hx = ((c.x + d.x) / 2) * W, hy = ((c.y + d.y) / 2) * H;
  return { x: (sx + hx) / 2, y: (sy + hy) / 2, len: Math.hypot(sx - hx, sy - hy) };
}

/** Two readings of one trunk (see above). `width` in pixels. */
function sameTrunk(a, b, width, W, H, T) {
  const p = trunk(a, W, H, T), q = trunk(b, W, H, T);
  if (!p || !q || !p.len || !q.len) return false;
  if (Math.min(p.len, q.len) / Math.max(p.len, q.len) < T.TORSO_RATIO) return false;
  return Math.hypot(p.x - q.x, p.y - q.y) <= T.TORSO_GAP * width;
}

/** The outline of the points the model saw clearly, in pixels, or null. */
function outline(lm, W, H) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, n = 0;
  for (let k = 0; k < Math.min(lm.length, 29); k++) {
    const p = lm[k];
    if (!p || vis(p) < 0.5 || !Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    n++;
    x0 = Math.min(x0, p.x * W); x1 = Math.max(x1, p.x * W); y0 = Math.min(y0, p.y * H); y1 = Math.max(y1, p.y * H);
  }
  return n >= 3 && x1 > x0 && y1 > y0 ? { x0, y0, x1, y1 } : null;
}

/** `small` is a skeleton squeezed inside `big` (see above). Widths in pixels. */
function nested(small, big, width, W, H, T) {
  if (vis(small[0]) < T.NOSE_VIS || vis(big[0]) < T.NOSE_VIS) return false;
  const nose = Math.hypot((small[0].x - big[0].x) * W, (small[0].y - big[0].y) * H) / width;
  if (nose <= T.NOSE) return true;
  if (nose > T.NEST_NOSE) return false;
  const a = outline(small, W, H), b = outline(big, W, H);
  if (!a || !b) return false;
  const ix = Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0)), iy = Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0));
  if ((ix * iy) / ((a.x1 - a.x0) * (a.y1 - a.y0)) < T.NEST_IN) return false;
  const [ls, rs, lh, rh] = [big[11], big[12], small[23], small[24]];
  if (![ls, rs, lh, rh].every((p) => p && Number.isFinite(p.y))) return false;
  return (lh.y + rh.y) / 2 <= (ls.y + rs.y) / 2;
}

/** A body's width in pixels: the shoulders, or half the torso when the shoulders are seen side on. */
function bodyWidth(lm, W, H) {
  const a = lm[11], b = lm[12], c = lm[23], d = lm[24];
  if (!a || !b) return 0;
  const sw = Math.hypot((a.x - b.x) * W, (a.y - b.y) * H);
  const torso = c && d ? Math.hypot(((a.x + b.x) - (c.x + d.x)) / 2 * W, ((a.y + b.y) - (c.y + d.y)) / 2 * H) : 0;
  return Math.max(sw, 0.5 * torso);
}

/** Height of the body's points in pixels: shoulders to ankles, or as far down as the model placed them. */
function span(lm, W, H) {
  let y0 = Infinity, y1 = -Infinity, x0 = Infinity, x1 = -Infinity;
  for (const k of [0, 11, 12, 23, 24, 25, 26, 27, 28]) {
    const p = lm[k];
    if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    y0 = Math.min(y0, p.y * H); y1 = Math.max(y1, p.y * H); x0 = Math.min(x0, p.x * W); x1 = Math.max(x1, p.x * W);
  }
  return y1 > y0 ? Math.hypot(y1 - y0, x1 - x0) : 0;
}

/**
 * The indices of the poses to keep, in their original order: every pose,
 * except a copy of a person already kept. `W`, `H` make the distances pixels,
 * so a wide frame does not stretch them.
 */
export function distinctPoses(poses, W = 1, H = 1, T = POSES) {
  if (!Array.isArray(poses) || poses.length < 2) return (poses || []).map((_, i) => i);
  // The copy is the model fitting a second, smaller skeleton inside the same
  // person, so the bigger body is kept.
  const size = (lm) => (Array.isArray(lm) ? span(lm, W, H) : 0);
  const order = poses.map((_, i) => i).sort((a, b) => size(poses[b]) - size(poses[a]));
  const kept = [];
  for (const i of order) {
    const a = poses[i];
    const copy = Array.isArray(a) && kept.some((j) => {
      const b = poses[j];
      const width = Math.max(bodyWidth(a, W, H), bodyWidth(b, W, H));
      if (!width) return false;
      const gaps = T.POINTS.map((k) => (a[k] && b[k] ? Math.hypot((a[k].x - b[k].x) * W, (a[k].y - b[k].y) * H) : Infinity)).sort((x, y) => x - y);
      // `b` was kept first, so it is the bigger body.
      return gaps[gaps.length >> 1] < T.SAME * width || nested(a, b, width, W, H, T) || sameTrunk(a, b, width, W, H, T);
    });
    if (!copy) kept.push(i);
  }
  return kept.sort((a, b) => a - b);
}

/* ------------------------------------------------------------ hands or not
 * Whether a device can afford the hand models at all (public/js/trackengine.js
 * HANDS_BUILD). `poseMs` are the pose model's times on the frames so far, the
 * first of which compiles the GPU's shaders and is not counted. 'wait' until
 * `after` frames beyond that first one are in, then 'build' if their median is
 * at most `maxPoseMs`, else 'skip'.
 */
export function handsVerdict(poseMs, { after = 5, maxPoseMs = 250 } = {}) {
  const ms = (Array.isArray(poseMs) ? poseMs : []).filter((v) => Number.isFinite(v));
  if (after <= 0) return 'build';
  if (ms.length <= after) return 'wait';
  const s = ms.slice(1).sort((a, b) => a - b);
  return s[s.length >> 1] <= maxPoseMs ? 'build' : 'skip';
}

/* ------------------------------------------------------------ scheduling
 * One camera frame, one decision: what the models run on it. The pose runs on
 * every frame it can, because the shot detector reads it. The hands are for
 * the overlay only, so they are fitted around the pose. When pose and hands
 * fit in one frame's time together, both run on every frame. When they do not,
 * it depends on where the models run:
 *
 *   'together' (the tracking worker, public/js/track-worker.js): the hands ride
 *     along with the pose on one frame in `every`, chosen so that an average
 *     frame still fits: pose + hands / every <= the frame's time. The pose runs
 *     on every frame and the hand squares are cut from that same frame's pose.
 *     A frame that carries both answers a little later, but nothing waits on
 *     it: the page's thread never runs a model. The hands keep running through
 *     a shot, which is when they are worth watching.
 *   'apart' (the models on the page's own thread, where a browser cannot run
 *     them in a worker): never both in one frame, because there a long frame
 *     is a frozen page. The hands take one frame in `every` on their own, cut
 *     from the last pose. While a shot is being read the pose keeps every
 *     frame, so the release is never read from a frame the pose skipped, and
 *     the hands ride along with it on one frame in `maxEvery` (the Body Map's
 *     finger readings need hand frames around the release). Those few frames
 *     run long on the page, for the second or so a shot lasts. (Giving the models only 60%
 *     of a frame on the page was tried on 2026-09-26: the page's frames got
 *     shorter, 16 ms against 26, but the hands ran 3 to 5 times a second and
 *     were drawn on 6 to 12% of the frames with a wrist in view, against 95%.
 *     Not kept: the page path is the fallback, and it keeps its hands.)
 *
 * Times are the models' own, measured by whoever runs them, smoothed so one
 * slow frame does not flip the plan.
 */
// In the worker a frame that carries pose and hands only answers later; the page never waits on it.
// So the hands ride on every frame while both together take up to STRETCH of a camera frame: a few
// tracked frames a second are worth less than hands that keep up (2026-10-10: a hand drawn from a
// reading two to four frames old is what "not responsive" looked like).
export const STRETCH = 1.6;
export function createCadence({ budgetMs = 33, maxEvery = 4, smooth = 0.2, mode = 'apart' } = {}) {
  let poseMs = null, handsMs = null, n = 0, budget = budgetMs, how = mode;
  const ema = (old, v) => (old === null ? v : old + smooth * (v - old));
  return {
    get poseMs() { return poseMs; },
    get handsMs() { return handsMs; },
    get budget() { return budget; },
    get mode() { return how; },
    /** 'together' or 'apart': see above. */
    setMode(m) { if (m === 'together' || m === 'apart') how = m; },
    /** The frame interval the camera is actually delivering, in ms. */
    setBudget(ms) { if (Number.isFinite(ms) && ms > 5 && ms < 200) budget = ms; },
    /** Measured model times for the frame just processed. Null means that model did not run. */
    note({ pose = null, hands = null } = {}) {
      if (Number.isFinite(pose)) poseMs = ema(poseMs, pose);
      if (Number.isFinite(hands)) handsMs = ema(handsMs, hands);
    },
    /** How often the hands run, 1 = every frame. */
    every({ shooting = false } = {}) {
      if (poseMs === null || handsMs === null) return 1;
      if (poseMs + handsMs <= budget * (how === 'together' ? STRETCH : 1)) return 1;
      if (shooting && how === 'apart') return maxEvery;
      const spare = budget - poseMs;
      if (spare <= 0) return maxEvery;
      // together: the hands' time spread over `every` frames fits in what the
      // pose leaves. apart: the hands need a frame of their own as well.
      const k = how === 'together' ? Math.ceil(handsMs / spare) : Math.ceil(handsMs / spare) + 1;
      return Math.min(maxEvery, Math.max(2, k));
    },
    /** What runs on the next frame: { pose, hands }. `hands` false when there is nothing to look for. */
    next({ shooting = false, hands = true } = {}) {
      n++;
      if (!hands) return { pose: true, hands: false };
      const every = this.every({ shooting });
      if (every === 1) return { pose: true, hands: true };
      if (!Number.isFinite(every)) return { pose: true, hands: false };
      const handsTurn = n % every === 0;
      // During a shot the page path rides along too, so the pose keeps every frame.
      return how === 'together' || shooting ? { pose: true, hands: handsTurn } : { pose: !handsTurn, hands: handsTurn };
    },
    reset() { poseMs = null; handsMs = null; n = 0; },
  };
}

/**
 * Graphics chip (GPU) or processor (CPU) for the models, from pose times measured on the one in use
 * (app.js). Only frames after the hand models have settled count: building them slows the GPU's
 * frames for a moment, and on 2026-10-05 an early version switched devices to the slower CPU path on
 * exactly those frames and kept it for 30 days. A slow GPU earns a CPU trial, not a switch: the CPU
 * is kept only if it is clearly faster than the GPU it replaced (CPU_MUST_BEAT of its time).
 *   on      'GPU' | 'CPU', the one these times came from
 *   times   pose ms per frame, after the hands settled
 *   gpuMs   the GPU's median from the trial that led here, if any
 * Returns null until there are enough frames, else { next: 'GPU' | 'CPU' | 'try-CPU', median }.
 */
export const DELEGATE = { SKIP: 10, FRAMES: 15, SLOW_GPU_MS: 120, CPU_MUST_BEAT: 0.8 };
export function delegateVerdict({ on, times = [], gpuMs = null }) {
  const v = times.filter(Number.isFinite);
  if (v.length < DELEGATE.SKIP + DELEGATE.FRAMES) return null;
  const s = v.slice(DELEGATE.SKIP).sort((a, b) => a - b);
  const median = s[Math.floor(s.length / 2)];
  if (on === 'GPU') return { next: median > DELEGATE.SLOW_GPU_MS ? 'try-CPU' : 'GPU', median };
  if (!Number.isFinite(gpuMs)) return { next: 'CPU', median };   // no GPU worth the name: nothing to compare
  return { next: median <= gpuMs * DELEGATE.CPU_MUST_BEAT ? 'CPU' : 'GPU', median };
}
